import React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { defineMessages, FormattedMessage } from 'react-intl';

import { callbackHooks } from '@nxte-nl/callback-hooks';
import { stateHooks } from '@nxte-nl/state-hooks/src/stateHooks';

import {
  BusinessOfferForm,
  FormFields,
} from '../../components/BusinessOfferForm/BusinessOfferForm';
import { BusinessOfferFormV1 } from '../../components/BusinessOfferFormV1/BusinessOfferFormV1';
import { SuccessModal } from '../../components/SuccessModal/SuccessModal';
import { dataLayerV2Hooks,
  FormLocationDataLayer } from '../../hooks/dataLayerHooks/dataLayerV2Hooks';
import { featureStateHooks } from '../../hooks/featureStateHooks/featureStateHooks';
import { layoutHooks } from '../../hooks/layoutHooks/layoutHooks';
import { messageHooks } from '../../hooks/messageHooks/messageHooks';
import {
  sendProposalRequestHooks,
} from '../../hooks/mutationHooks/sendProposalRequestHooks/sendProposalRequestHooks';
import { GlobalMessages } from '../../types/Messages';
import { hashingUtils } from '../../utils/hashingUtils/hashingUtils';
import { GOOGLE_CLICK_ID } from '../GoogleClickIdContainer/GoogleClickIdContainer';

const messages = defineMessages({
  buttonTitle: {
    id: 'src.components.BusinessOfferContainer.buttonTitle',
    defaultMessage: 'Ok',
  },
  successMessage: {
    id: 'src.components.BusinessOfferContainer.successMessage',
    defaultMessage: '[[Thank you for your application. We have received this in good order and will send the quotation by email within 1 hour.]]',
  },
});

export type UseHandleCloseSuccessModalParams = {
  onCloseCallback?: () => void;
  onCloseSuccessModal: () => void;
};

export const hooks = {
  useSubmitForm(formLocation: FormLocationDataLayer) {
    const { executeRecaptcha } = useGoogleReCaptcha();

    const {
      value: isOpenSuccessModal,
      setTrue: onOpenSuccessModal,
      setFalse: onCloseSuccessModal,
    } = stateHooks.useBooleanState();

    const { mutate, loading } = sendProposalRequestHooks
      .useSendProposalRequestMutation(onOpenSuccessModal);

    const trackFormSubmitQuotationEvent = dataLayerV2Hooks.useTrackFormSubmitQuotation();

    const setLoading = messageHooks.useNextWithValue(GlobalMessages.MutationInProgress, loading);
    callbackHooks.useEffectCallback(setLoading);

    const onSubmit = React.useCallback(
      async (data: FormFields, phoneCode: string) => {
        if (executeRecaptcha) {
          const captchaToken = await executeRecaptcha(CONFIG.reCaptchaSendProposalAction);

          if (captchaToken) {
            mutate({
              variables: {
                companyName: data.companyName,
                firstName: data.firstName,
                email: data.email,
                phone: phoneCode + data.phoneNumber,
                message: data.message,
                captchaToken,
                googleClickId: localStorage.getItem(GOOGLE_CLICK_ID) || null,
              },
            });

            const hashedEmail = await hashingUtils.sha256(data.email);
            const hashedPhoneNumber = await hashingUtils.sha256(`${phoneCode}${data.phoneNumber}`);

            trackFormSubmitQuotationEvent({
              formLocation,
              email: hashedEmail,
              phoneNumber: hashedPhoneNumber,
            });
          }
        }
      },
      [executeRecaptcha, formLocation, mutate, trackFormSubmitQuotationEvent],
    );

    return {
      onSubmit,
      isOpenSuccessModal,
      onCloseSuccessModal,
    };
  },
  useHandleCloseSuccessModal({
    onCloseSuccessModal,
    onCloseCallback,
  }: UseHandleCloseSuccessModalParams) {
    const handleCloseSuccessModal = React.useCallback(() => {
      onCloseSuccessModal();
      onCloseCallback?.();
    }, [onCloseCallback, onCloseSuccessModal]);
    return { handleCloseSuccessModal };
  },
};

export type Props = {
  formLocation: FormLocationDataLayer;
  isFocusToTheFirstField?: boolean;
  isTwoColumnsLayout?: boolean;
  offerBlockSubtitle: string | null;
  offerBlockTitle: string | null;
  onCloseCallback?: () => void;
  resetFocusToTheFirstField?: () => void;
};

export const BusinessOfferContainer = React.memo((props: Props) => {
  const {
    offerBlockTitle,
    offerBlockSubtitle,
    resetFocusToTheFirstField,
    isFocusToTheFirstField,
    onCloseCallback,
    isTwoColumnsLayout,
    formLocation,
  } = props;

  const {
    onSubmit,
    isOpenSuccessModal,
    onCloseSuccessModal,
  } = hooks.useSubmitForm(formLocation);

  const { handleCloseSuccessModal } = hooks.useHandleCloseSuccessModal({
    onCloseSuccessModal,
    onCloseCallback,
  });

  const submitInProgress = messageHooks.useSubjectState(GlobalMessages.MutationInProgress);

  const { isDesktop } = layoutHooks.useDesktop();

  const { isPDPBusinessModalUsps } = featureStateHooks.useFeatureToggles();

  return (
    <>
      <SuccessModal
        isOpen={isOpenSuccessModal}
        onClose={handleCloseSuccessModal}
        onButtonClick={handleCloseSuccessModal}
        buttonText={<FormattedMessage {...messages.buttonTitle} />}
        message={<FormattedMessage {...messages.successMessage} />}
      />

      {isTwoColumnsLayout ? (
        <BusinessOfferFormV1
          isDesktop={isDesktop}
          isPDPBusinessModalUsps={isPDPBusinessModalUsps}
          onSubmit={onSubmit}
          submitInProgress={submitInProgress}
          title={offerBlockTitle}
          subtitle={offerBlockSubtitle}
          isFocusToTheFirstField={isFocusToTheFirstField}
          resetFocusToTheFirstField={resetFocusToTheFirstField}
        />
      ) : (
        <BusinessOfferForm
          isPDPBusinessModalUsps={isPDPBusinessModalUsps}
          onSubmit={onSubmit}
          submitInProgress={submitInProgress}
          title={offerBlockTitle}
          subtitle={offerBlockSubtitle}
          isFocusToTheFirstField={isFocusToTheFirstField}
          resetFocusToTheFirstField={resetFocusToTheFirstField}
        />
      )}
    </>
  );
});

BusinessOfferContainer.displayName = 'BusinessOfferContainer';
